import React, { useRef } from 'react';
import { Button, Card, Col, Image, Popconfirm, Space, Tag, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined, HolderOutlined } from '@ant-design/icons';
import { useDrag, useDrop } from 'react-dnd';

const ItemTypes = {
    TICKET_TYPE_CARD: 'ticket_type_card',
};

const DragDropTicketTypeCard = ({ 
    ticket_type, 
    index,
    moveCard,
    handleDrop,
    openEditModal, 
    deleteParkApplicableTicketType,
    isUpdating = false,
}) => {
    const ref = useRef(null);
    const { image, descriptions, park_applicable_ticket_type_id, sequence } = ticket_type?.ParkApplicableTicketType;
    
    const [{ handlerId }, drop] = useDrop({
        accept: ItemTypes.TICKET_TYPE_CARD,
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover(item, monitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return;
            }

            // Determine rectangle on screen
            const hoverBoundingRect = ref.current?.getBoundingClientRect();

            // Get horizontal and vertical middle
            const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

            // Determine mouse position
            const clientOffset = monitor.getClientOffset();

            // Get pixels to the top and left
            const hoverClientX = clientOffset.x - hoverBoundingRect.left;
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            // Determine if we're moving in a grid (cards can be side by side)
            // We need to check both X and Y coordinates for grid layouts
            
            // Calculate if we should swap based on position
            let shouldSwap = false;

            if (dragIndex < hoverIndex) {
                // Dragging forward (left to right, or top to bottom)
                // Only swap when cursor passes the middle
                shouldSwap = hoverClientX > hoverMiddleX || hoverClientY > hoverMiddleY;
            } else {
                // Dragging backward (right to left, or bottom to top)
                // Only swap when cursor passes the middle from the other side
                shouldSwap = hoverClientX < hoverMiddleX || hoverClientY < hoverMiddleY;
            }

            if (!shouldSwap) {
                return;
            }

            // Time to actually perform the visual move
            moveCard(dragIndex, hoverIndex);

            // Note: we're mutating the monitor item here!
            // Generally it's better to avoid mutations,
            // but it's good here for the sake of performance
            // to avoid expensive index searches.
            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag, preview] = useDrag({
        type: ItemTypes.TICKET_TYPE_CARD,
        item: () => {
            return { park_applicable_ticket_type_id, index };
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        end: (item, monitor) => {
            // This is called when the drag ends (mouse released)
            const didDrop = monitor.didDrop();
            // Call the API to save the new order regardless of didDrop
            // because hover already moved the items
            handleDrop();
        },
        canDrag: !isUpdating,
    });

    const opacity = isDragging ? 0.4 : 1;

    // Connect drag and drop to the ref
    drag(drop(ref));

    return (
        <Col span={4} xs={24} sm={8} md={6} xl={4}>
            <div 
                ref={ref} 
                style={{ 
                    opacity, 
                    cursor: isUpdating ? 'not-allowed' : (isDragging ? 'grabbing' : 'grab'),
                    transition: 'opacity 0.2s ease',
                }}
                data-handler-id={handlerId}
            >
                <Card
                    style={{ height: '100%' }}
                    styles={{ body: { padding: 12, paddingTop: 12, height: 160 } }}
                    cover={
                        <div style={{ padding: 8, paddingBottom: 0 }}>
                            <div style={{ height: '100%', width: 'auto', textAlign: 'center' }}>
                                <div style={{ background: 'var(--secondary-button-color)', maxWidth: '100%' }}>
                                    {image ? (
                                        <Image 
                                            preview={false} 
                                            src={image} 
                                            alt={ticket_type?.name}
                                            style={{ objectFit: 'cover', height: 120, borderRadius: 0 }} 
                                        />
                                    ) : (
                                        <div style={{ height: 120 }} />
                                    )}
                                </div>
                            </div>
                        </div>
                    }
                >
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <div style={{ textAlign: 'center' }}>
                            <div>
                                <span className='header-font' style={{ fontWeight: '500', color: '#434343' }}>
                                    {ticket_type?.name}
                                </span>
                            </div>
                            {descriptions && (
                                <div>
                                    <span style={{ color: '#6f7287', fontSize: 12 }}>
                                        {descriptions}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div style={{ marginTop: 'auto' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                {sequence && (
                                    <div>
                                        <Tag color='geekblue'>{sequence}</Tag>
                                    </div>
                                )}
                                <Tooltip title={isUpdating ? "Updating..." : "Drag to reorder"}>
                                    <div 
                                        style={{ 
                                            cursor: isUpdating ? 'not-allowed' : 'grab',
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '4px',
                                            color: isUpdating ? '#ccc' : 'var(--secondary-text-color)',
                                            marginLeft: 4,
                                        }}
                                    >
                                        <HolderOutlined style={{ fontSize: 16 }} />
                                    </div>
                                </Tooltip>
                                <div style={{ marginLeft: 'auto' }}>
                                    <Space>
                                        <Tooltip title="Edit">
                                            <Button
                                                icon={<EditOutlined style={{ color: 'var(--secondary-text-color)' }} />}
                                                size='small'
                                                onClick={() => openEditModal(park_applicable_ticket_type_id)}
                                                disabled={isUpdating}
                                            />
                                        </Tooltip>
                                        <Popconfirm
                                            title="Delete Ticket Type"
                                            description={`Are you sure you want to delete "${ticket_type?.name}"?`}
                                            overlayStyle={{ minWidth: 250 }}
                                            onConfirm={() => deleteParkApplicableTicketType(park_applicable_ticket_type_id)}
                                            okText="Yes"
                                            cancelText="No"
                                            disabled={isUpdating}
                                        >
                                            <Tooltip title="Delete">
                                                <Button
                                                    icon={<DeleteOutlined style={{ color: 'var(--secondary-text-color)' }} />}
                                                    size='small'
                                                    disabled={isUpdating}
                                                />
                                            </Tooltip>
                                        </Popconfirm>
                                    </Space>
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        </Col>
    );
};

export default DragDropTicketTypeCard;